<template>
	<div class="p4" style="height:100vh; overflow: scroll;">
		<!-- order -->
		<p class="xs-12 fs4 fw7 lh125">{{ $t("order") }}</p>
		<j-base-input-number class="mb6" v-model="clauseOrder" />

		<!-- Type selection -->
		<div class="flex xs-right mb6">
			<j-button
				class="mr1"
				size="xsmall"
				:type="type === 'content' ? 'secondary' : 'bordered'"
				@click="type = 'content'"
				>{{ $t("json_object") }}</j-button
			>
			<j-button
				size="xsmall"
				:type="type === 'variables' ? 'secondary' : 'bordered'"
				@click="type = 'variables'"
				>{{ $t("graphql_variables") }}</j-button
			>
		</div>

		<!-- selection -->
		<div class="flex xs-middle xs-right mb6">
			<j-base-toggle size="xsmall" v-model="isOverride" />
			<div class="fw7 ml2">{{ $t("override") }}</div>
		</div>

		<!-- disclaimer -->
		<div
			class="p2 bgWarning fs3 lh150"
			v-if="
				hasHtmlOverride &&
					!isOverride &&
					contentJson &&
					Object.keys(contentJson).length
			"
		>
			This clause has been overriden so the dynamic content will be ignored.
			Switch to override mode if you want to edit the content
		</div>

		<!-- contentJson -->
		<p
			v-if="!isOverride && contentJson && Object.keys(contentJson).length"
			class="xs-12 fs4 fw7 lh125 mt4 mb2"
		>
			{{ $t("dynamic_content") }}
		</p>
		<div
			v-show="type === 'content' && !isOverride"
			v-for="(item, key) in contentJson"
			:key="key"
			class="xs-middle mb3"
		>
			<!-- Label -->
			<div class="xs-12 fs4 fw4 mb1">{{ key }}</div>
			<!-- value -->
			<div class="xs-12 pr1">
				<core-form-input
					:componentName="
						checkIsNum(contentJson[key])
							? 'j-base-input-number'
							: 'j-base-input-text'
					"
					:name="`name-${key}`"
					size="small"
					:value="contentJson[key]"
					@input="handleInput(key, $event)"
				/>
			</div>
		</div>

		<!-- VariablesJson -->
		<div v-show="type === 'variables' && !isOverride">
			<p class="fs4 fw7 c1 mb1">{{ $t("graphql_variables") }}</p>
			<core-monaco-editor language="json" v-model="variablesJson" />
		</div>

		<!-- GraphQl Query -->
		<j-card v-show="type === 'variables' && !isOverride" class="mt2">
			<j-expendable-box>
				<div slot="header" class="pl2 fs4 fw7">
					{{ `${$t("graphql_query")} (${$t("read_only")})` }}
				</div>
				<div slot="body">
					<core-monaco-editor
						language="graphql"
						:value="clause.template.graphqlQuery"
						:readOnly="true"
					/>
				</div>
			</j-expendable-box>
		</j-card>

		<!-- Override area -->
		<p v-if="isOverride" class="xs-12 fs4 fw7 lh125 mt4 mb2">HTML</p>
		<j-spinner v-if="isRendering" />
		<core-rich-text-editor
			v-if="isOverride && !isRendering"
			:content="htmlOverride"
			@input="updateHtml"
		/>

		<div class="flex xs-12 xs-right mt6">
			<j-button @click="onUpdate">{{ $t("update") }}</j-button>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JCard from "@/components/JCard"
import JExpendableBox from "@/components/JExpendableBox"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreRichTextEditor from "@/modules/core/components/CoreRichTextEditor"
import JBaseToggle from "@/components/JBaseToggle"
import JSpinner from "@/components/JSpinner"
import JBaseInputNumber from "@/components/JBaseInputNumber"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JButton,
		JCard,
		JExpendableBox,
		CoreMonacoEditor,
		CoreFormInput,
		CoreRichTextEditor,
		JBaseToggle,
		JSpinner,
		JBaseInputNumber
	},

	props: {
		caseId: String,
		proposalId: String,
		offerId: String,
		clause: Object
	},

	data: () => ({
		type: "content", //content or variables
		contentJson: null,
		variablesJson: null,
		clauseOrder: null,
		isOverride: false,
		htmlOverride: null,
		isRendering: false,
		hasHtmlOverride: false
	}),

	methods: {
		async onUpdate() {
			const query = gql`
				mutation updateClauseOfProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$clauseId: String!
					$input: updateClauseInput!
				) {
					updateClauseOfProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						clauseId: $clauseId
						input: $input
					) {
						status
						errors
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				clauseId: this.clause.id,
				input: {
					templateId: this.clause.template.id,
					order: this.clauseOrder,
					...(this.isOverride && { htmlOverride: this.htmlOverride }),
					renderParameters: {
						name: "data",
						contentJsonString: JSON.stringify(this.contentJson),
						variablesJsonString: this.variablesJson
					}
				}
			}
			const res = await api({ query, variables })
			if (res?.data?.updateClauseOfProposalOffer?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.updateClauseOfProposalOffer?.errors?.[0] || [
						this.$t("errors.general")
					]
				)
				return
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("clause_updated")
					}
				})
			this.$emit("updated")
		},

		handleInput(key, $event) {
			if (this.checkIsNum($event)) this.contentJson[key] = parseInt($event)
			else this.contentJson[key] = $event
		},

		checkIsNum(value) {
			return /^\d+$/.test(value)
		},

		updateHtml(input) {
			this.htmlOverride = input
		}
	},

	created() {
		this.contentJson = JSON.parse(
			this.clause?.renderParameters?.contentJsonString
		)
		this.variablesJson = this.clause?.renderParameters?.variablesJsonString
		this.clauseOrder = this.clause?.order
		this.hasHtmlOverride = Boolean(this?.clause?.htmlOverride)
		this.htmlOverride =
			this.clause?.htmlOverride || this.clause?.renderedHtmlResult?.value
	}
}
</script>
